/**
 * פרטי איש קשר / לקוח מתוך שורת הזמנה — aliases של LDS, דינמי, מלון ו-hrFlightPackage.
 * לא משתמש ב-user_email / user_first_name (שדות סוכן).
 */
import {
  buildOrderPassengerDisplayName,
  normalizeOrderPassengersInfoSlots,
  resolveOrderPassengersByRooms,
} from "./resolveOrderPassengersByRooms.js";

function nonEmpty(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s;
}

function asRecord(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

function firstAdultFromSlots(order) {
  const slots = normalizeOrderPassengersInfoSlots(order);
  const adults = Array.isArray(slots?.[0]?.adults) ? slots[0].adults : [];
  return adults.find((p) => p && typeof p === "object") || null;
}

function pickPhone(src) {
  if (!src || typeof src !== "object") return "";
  for (const key of ["cell", "phone", "Phone", "mobile", "tel", "clientphone", "client_phone"]) {
    const v = nonEmpty(src[key]);
    if (v) return v;
  }
  return "";
}

function pickEmail(src) {
  if (!src || typeof src !== "object") return "";
  for (const key of ["email", "Email", "mail", "customerEmail", "clientemail", "client_email"]) {
    const v = nonEmpty(src[key]);
    if (v && v.includes("@")) return v;
  }
  return "";
}

function pickNameFromRecord(src) {
  if (!src || typeof src !== "object") return "";
  const fromPax = buildOrderPassengerDisplayName(src);
  if (fromPax) return fromPax;
  const composite = [src.firstName, src.lastName, src.first_name, src.last_name]
    .filter((x) => nonEmpty(x))
    .map((x) => String(x).trim());
  if (composite.length >= 2) {
    return `${composite[0]} ${composite[1]}`.replace(/\s+/g, " ").trim();
  }
  for (const key of [
    "fullname",
    "fullName",
    "FullName",
    "contactname",
    "contactName",
    "clientname",
    "customerName",
    "name",
  ]) {
    const v = nonEmpty(src[key]);
    if (v) return v;
  }
  return "";
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {{
 *   name: string,
 *   phone: string,
 *   email: string,
 *   nameSource: string,
 *   phoneSource: string,
 *   emailSource: string,
 * }}
 */
export function resolveOrderCustomerContact(order) {
  const empty = {
    name: "",
    phone: "",
    email: "",
    nameSource: "empty",
    phoneSource: "empty",
    emailSource: "empty",
  };
  if (!order || typeof order !== "object") return empty;

  const clients =
    asRecord(order.clientsinfo) || asRecord(order.clientsInfo) || {};
  const hr =
    asRecord(clients.hrFlightPackage) ||
    asRecord(order.hrFlightPackage) ||
    {};
  const firstAdult = firstAdultFromSlots(order);
  const namedPax = resolveOrderPassengersByRooms(order)
    .rooms.flatMap((room) => room.passengers || [])
    .find((p) => nonEmpty(p?.displayName));

  let name = "";
  let nameSource = "empty";
  const tryName = (val, src) => {
    const v = nonEmpty(val);
    if (!v) return false;
    name = v;
    nameSource = src;
    return true;
  };

  if (
    !tryName(buildOrderPassengerDisplayName(firstAdult), "passengersinfo.first_adult") &&
    !tryName(namedPax?.displayName, "resolved_passengers") &&
    !tryName(pickNameFromRecord(clients), "clientsinfo") &&
    !tryName(pickNameFromRecord(hr), "clientsinfo.hrFlightPackage") &&
    !tryName(order.fullname, "order.fullname") &&
    !tryName(order.contactname, "order.contactname") &&
    !tryName(order.clientname, "order.clientname") &&
    !tryName(order.customerName, "order.customerName")
  ) {
    name = "";
    nameSource = "empty";
  }

  let phone = "";
  let phoneSource = "empty";
  const tryPhone = (val, src) => {
    const v = nonEmpty(val);
    if (!v) return false;
    phone = v;
    phoneSource = src;
    return true;
  };

  if (
    !tryPhone(pickPhone(firstAdult), "passengersinfo.first_adult") &&
    !tryPhone(pickPhone(clients), "clientsinfo") &&
    !tryPhone(pickPhone(hr), "clientsinfo.hrFlightPackage") &&
    !tryPhone(order.cell, "order.cell") &&
    !tryPhone(order.phone, "order.phone") &&
    !tryPhone(order.clientphone, "order.clientphone")
  ) {
    phone = "";
    phoneSource = "empty";
  }

  let email = "";
  let emailSource = "empty";
  const tryEmail = (val, src) => {
    const v = nonEmpty(val);
    if (!v || !v.includes("@")) return false;
    email = v;
    emailSource = src;
    return true;
  };

  if (
    !tryEmail(pickEmail(firstAdult), "passengersinfo.first_adult") &&
    !tryEmail(pickEmail(clients), "clientsinfo") &&
    !tryEmail(pickEmail(hr), "clientsinfo.hrFlightPackage") &&
    !tryEmail(order.email, "order.email") &&
    !tryEmail(order.clientemail, "order.clientemail") &&
    !tryEmail(order.customerEmail, "order.customerEmail")
  ) {
    email = "";
    emailSource = "empty";
  }

  return { name, phone, email, nameSource, phoneSource, emailSource };
}
